import Dexie, { type Table } from 'dexie';
import type { Ergebnis, GespeichertesErgebnis, QrInfo } from '../shared/api.js';
import type { Geraeteklasse, Modus, PruefpunktKey } from '../shared/pruefpunkte.js';

/** Ergebnis, das noch zum Server muss; gesendet wird mit dem Token des Modus, in dem es entstand. */
export interface AusgangEintrag extends Ergebnis {
  modus: Modus;
}

/** Was die App je Prüfpunkt anzeigt: lokal erfasst oder vom Server. */
export interface AngezeigtesErgebnis extends Ergebnis {
  modus: Modus;
  quelle: 'geraet' | 'server';
  gesendet: boolean;
}

export interface TestAnlage {
  id: string;
  nr: number;
  code: string;
  bezeichnung: string;
  standort: string;
  hersteller: string;
  modell: string;
  seriennummer: string;
  baujahr: number;
  naechstePruefung: string;
  verlauf: { datum: string; text: string }[];
}

export interface Vorschaubild {
  id: string;
  nr: number;
  bild: Blob;
}

export interface TestbestandMeta {
  schluessel: 'meta';
  angelegtAm: string;
  anlagen: number;
  bilder: number;
  pruefsumme: string;
  bytes: number;
}

export type FotoStatus = 'wartet' | 'gesendet' | 'fehler';

export interface WartendesFoto {
  id: string;
  serieId: string;
  nr: number;
  bild: Blob | null;
  groesse: number;
  pruefsumme: string;
  status: FotoStatus;
  versuche: number;
  letzterFehler?: string;
}

export interface FotoSerie {
  id: string;
  anzahl: number;
  modus: Modus;
  bremse: boolean;
  status: 'erzeugen' | 'laeuft' | 'fertig' | 'abgebrochen';
  erstelltAm: string;
  gestartetAm?: string;
  fertigAm?: string;
  unterbrechungen: { zeit: string; gesendet: number }[];
}

export interface ProtokollEintrag {
  id?: number;
  zeit: string;
  art: string;
  daten: unknown;
}

interface Einstellung {
  schluessel: string;
  wert: unknown;
}

class GeraetetestDb extends Dexie {
  einstellungen!: Table<Einstellung, string>;
  ausgang!: Table<AusgangEintrag, string>;
  ergebnisse!: Table<AngezeigtesErgebnis, PruefpunktKey>;
  anlagen!: Table<TestAnlage, string>;
  vorschaubilder!: Table<Vorschaubild, string>;
  testbestand!: Table<TestbestandMeta, string>;
  fotos!: Table<WartendesFoto, string>;
  serien!: Table<FotoSerie, string>;
  protokoll!: Table<ProtokollEintrag, number>;

  constructor() {
    super('tfm-geraetetest');
    this.version(1).stores({
      einstellungen: 'schluessel',
      ausgang: 'id',
      ergebnisse: 'pruefpunkt',
      anlagen: 'id',
      vorschaubilder: 'id',
      testbestand: 'schluessel',
      fotos: 'id, serieId, [serieId+status]',
      serien: 'id, status',
      protokoll: '++id, art',
    });
  }
}

export const db = new GeraetetestDb();

/** Einstellungen mit festen Typen. */
export interface Einstellungen {
  'token:app': string;
  'token:browser': string;
  /** Zugang nur für Auswertung und Druckseite, ohne Testgerät */
  'token:auswertung': string;
  'geraetId:app': string;
  'geraetId:browser': string;
  name: string;
  klasse: Geraeteklasse;
  starts: number;
  letzterStart: string;
  zuletztAktiv: string;
  qr: QrInfo;
  vapidPublicKey: string;
  kameraErlaubtInStart: number;
  gegenprobe: { zeit: string; sekunden: number | null };
  speicherAusBrowser: boolean;
  installationGemeldet: string;
}

export async function lies<K extends keyof Einstellungen>(schluessel: K): Promise<Einstellungen[K] | undefined> {
  const eintrag = await db.einstellungen.get(schluessel);
  return eintrag?.wert as Einstellungen[K] | undefined;
}

export async function schreibe<K extends keyof Einstellungen>(schluessel: K, wert: Einstellungen[K]): Promise<void> {
  await db.einstellungen.put({ schluessel, wert });
}

export async function entferne(schluessel: keyof Einstellungen): Promise<void> {
  await db.einstellungen.delete(schluessel);
}

export async function protokolliere(art: string, daten: unknown): Promise<void> {
  await db.protokoll.add({ zeit: new Date().toISOString(), art, daten });
}

export function alsAngezeigt(e: GespeichertesErgebnis): AngezeigtesErgebnis {
  return {
    id: e.id,
    pruefpunkt: e.pruefpunkt,
    status: e.status,
    text: e.text,
    ...(e.details ? { details: e.details } : {}),
    erfasstAm: e.erfasstAm,
    modus: e.modus,
    quelle: e.quelle,
    gesendet: true,
  };
}
